import Axios from 'axios'
import classnames from 'classnames'
import DateRelative from '../../libs/translationsUtils/dateRelative'
import { Component } from 'preact'
import qs from 'qs'
import Tooltip from '../../control-components/ui-components/Tooltip'
import TopicReplyCount from '../../control-components/ui-components/TopicReplyCount'
import TopicViewsCount from '../../control-components/ui-components/TopicViewsCount'
import Phrase from '../../libs/translationsUtils/phrase'
import { t } from '../../libs/translationsUtils/translations'
import { CurrentUserContext } from '../../server-middleware/userContext'
import Icon from '../assets'
import LazyImage from '@insided/shared/uikit/LazyImage'
import UserAvatar from '../User/UserAvatar'
import UserName from '../User/UserName'
import { ContentType } from '@insided/shared/utils/contentType'
import IdeationStatus from './IdeationStatus'
import TopicContent, { getImageUrl } from './TopicContent'
import TopicLabel from './TopicLabel'
import TopicListLikeButton from './TopicListLikeButton'
import TopicTooltip from './TopicTooltip'

export type TopicType = 'post' | 'ideation'
interface PostWithoutTime {
  url: string
  content: string
  author: {
    id: number
    url: string
    userTitle: string
    name: string
    avatar: string
    rank: {
      avatarIconUrl: string
      name: string
    } | null
  }
}

type Post = PostWithoutTime & {createdAt: number, relativeCreationDate?: undefined} | PostWithoutTime & {relativeCreationDate: string, createdAt?: undefined }

export interface TopicInfo {
  firstPost: Post
  relevantPost: Post
  forum: {
    url: string
    title: string
  }
  featuredImage: string
  contentType: ContentType
  content_type?: ContentType
  type: TopicType
  isSticky: boolean
  id: number
  privateId: number
  url: string
  title: string
  numberOfViews: number
  publicLabel: string
  numberOfReplies: number
  numberOfLikes: number
  numberOfUnreadReplies: number|undefined
  lastPost: Post
  isTopicUnread: boolean|undefined
  hasCurrentUserLiked: boolean
  hasBestAnswer: boolean
  ideaStatus?: {
    name: string
    backgroundColor: string
    textColor: string
  }
}

interface Props {
  hideCategoryMetadata: boolean
  showUnsubscribeBtn?: boolean
  isActivityPage?: boolean
  topic: TopicInfo
  attachmentCdn: string
  csrfToken?: string
  clickCallback?: (topicId: number, id: number) => void
}

const getMetadataForTopicType = (topicType: TopicType, contentType: ContentType) => {
  if (topicType === 'ideation') {
    return <Phrase p='topic.list.metadata.shared.in' />
  } else {
    if (contentType === 'article') {
      return <Phrase p='topic.list.metadata.published.in' />
    } else if (contentType === 'question') {
      return <Phrase p='topic.list.metadata.asked.in' />
    }
    return <Phrase p='topic.list.metadata.posted.in' />
  }
}

const callCallback = (clickCallback: Function | undefined) => {
  if (typeof clickCallback === 'function') {
    clickCallback()
  }
}

class TopicListItemView extends Component<Props> {
  constructor(props: Props) {
    super(props)
    this.ref = null
    this.handleFavoriteRemoval = this.handleFavoriteRemoval.bind(this)
  }

  ref: HTMLDivElement | null

  async handleFavoriteRemoval() {
    await Axios.post(
      '/favorite/delete',
      qs.stringify({
        yip_csrf_token: this.props.csrfToken,
        threadid: this.props.topic.id
      })
    )
    if (this.ref) {
      this.ref.classList.add('remove-favorite')
      setTimeout(() => {
        if (this.ref) {
          this.ref.parentNode?.parentNode?.removeChild(this.ref.parentNode) // Remove `date-preact` component
        }
      }, 250)
    }
  }

  render() {
    const {
      topic,
      attachmentCdn,
      hideCategoryMetadata,
      clickCallback,
      showUnsubscribeBtn,
      isActivityPage
    } = this.props

    const {
      type,
      contentType,
      isSticky,
      id: topicId,
      relevantPost,
      url: topicUrl,
      featuredImage,
      title: topicTitle,
      numberOfViews,
      numberOfReplies,
      numberOfUnreadReplies,
      firstPost,
      forum,
      lastPost,
      isTopicUnread,
      hasCurrentUserLiked
    } = topic
    const author = firstPost.author
    const { userTitle } = author
    const { url: forumUrl, title: forumTitle } = forum
    const {
      url: lastPostUrl,
      author: lastPostAuthor,
      relativeCreationDate: lastPostRelativeCreationDate,
      createdAt: lastPostCreationDate
    } = lastPost
    const topicCategoryMetadata = getMetadataForTopicType(type, contentType)
    const redirectUrl: string = isActivityPage ? relevantPost.url : topic.url

    return (
      <CurrentUserContext.Consumer>
        {(user) => (
          <div
            className={classnames(
              'qa-topic-block',
              'topic-view',
              'js-thread',
              `js-thread-id-${topic.id}`,
              {
                'topic-view--unread': isTopicUnread,
                'topic-view--sticky': isSticky,
                'topic-view--ideation': type === 'ideation'
              }
            )}
            ref={(ref) => {
              this.ref = ref
            }}
          >
            <a className='topic-view_link' href={redirectUrl} />
            {isSticky && <Icon icon='stickyFlag' />}
            <div className='topic-view_header'>
              <div className='topic-view_header-item'>
                <UserAvatar user={author} extraClass='avatar' showRank />
              </div>
              <div
                className={classnames('topic-view_header-items-gathered', {
                  'topic-view_header-items_without-category': hideCategoryMetadata
                })}
              >
                <div className='topic-view_header-item'>
                  <UserName user={author} extraClass='username' />
                  {userTitle && (
                    <span className='user-title text--meta'>{userTitle}</span>
                  )}
                </div>
                {!hideCategoryMetadata && (
                  <a href={forumUrl} className='topic-view_header-item text--meta'>
                    <span>{topicCategoryMetadata}</span>&nbsp;
                    <span className='topic-view_header-item_category-link'>{forumTitle}</span>
                  </a>
                )}
              </div>
            </div>
            <div className='topic-view_info'>
              {featuredImage && (
                <LazyImage
                  type='background'
                  src={getImageUrl(topic.featuredImage, '480', attachmentCdn)}
                  className='thread-list-block__featured-image-link'
                />
              )}
              <div className='topic-view_body-container'>
                <div
                  className='topic-view_body'
                  onClick={() => {
                    callCallback(clickCallback)
                  }}
                >
                  <h4 className='thread-list-block__title'>
                    <a href={topicUrl} className='qa-topic-title thread-list-block__title-link'>
                      {topicTitle}
                    </a>

                    <TopicLabel topic={topic} />

                    {type === 'ideation'
                      ? <IdeationStatus topic={topic} />
                      : <TopicTooltip topic={topic} />}
                  </h4>

                  <TopicContent topic={{ ...topic, ...{ url: redirectUrl } }} />
                </div>
                <div className='topic-view_footer'>
                  {showUnsubscribeBtn && (
                    <Tooltip
                      trigger={
                        <i
                          aria-hidden='true'
                          className={classnames('icon--star-full', 'icon--auto-width', 'thread-meta-item__icon', 'thread-meta-item__text--highlight', 'qa-topic-meta-favorite-icon')}
                          onClick={this.handleFavoriteRemoval}
                        />
                      }
                      tooltipMessage={t('Forum', 'thread.unsubscribe')} //eslint-disable-line
                      qaClasses='qa-title-tooltip'
                    />
                  )}

                  <TopicListLikeButton
                    topic={topic}
                    isGuest={!user.id}
                    hasCurrentUserLiked={hasCurrentUserLiked}
                  />
                  {topic.contentType === 'article' || topic.contentType === 'productUpdate'
                    ? (
                      <span className={classnames('thread-meta-item', 'thread-meta-item--hover-highlight', 'is-hidden-S')}>
                        <a className='qa-topic-meta-views' href={topicUrl}>
                          <TopicViewsCount views={numberOfViews} />
                        </a>
                      </span>
                      )
                    : null}

                  <span className={classnames('thread-meta-item', 'thread-meta-item--comments', 'thread-meta-item--hover-highlight')}>
                    <a className='qa-topic-meta-replies-icon' href={lastPostUrl}>
                      <TopicReplyCount replyCount={numberOfReplies} />
                    </a>

                    {Boolean(numberOfUnreadReplies) && (
                      <span className='thread-meta-item__text thread-meta-bullet'>
                        <span className='icon--bullet icon--auto-width' />
                      </span>
                    )}
                    {Boolean(numberOfUnreadReplies) && (
                      <a
                        className='thread-meta-item__text--highlight qa-topic-meta-new'
                        href={`/topic/firstNewPostRedirect?topicId=${topicId}`}
                      >
                        <span className='thread-meta-item__text--highlight'>
                          <Phrase
                            p='forumList.meta.counter.post.user.new'
                            count={{ n: numberOfUnreadReplies || '' }}
                          />
                        </span>
                      </a>
                    )}
                  </span>

                  <span className={classnames('thread-meta-item', 'thread-meta-item--last-update', 'thread-meta-item--hover-highlight')}>
                    <UserAvatar
                      user={lastPostAuthor}
                      extraClass='avatar--XS thread-meta-item__icon'
                      showRank
                    />

                    <a className='qa-topic-meta-last-post-date-link' href={lastPostUrl}>
                      <span className='thread-meta-item__text'>
                        {lastPostCreationDate
                          ? <DateRelative dateTime={lastPostCreationDate} />
                          : lastPostRelativeCreationDate}
                      </span>
                    </a>
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </CurrentUserContext.Consumer>
    )
  }
}

export default TopicListItemView
